import type { JSX } from 'react';
import React, { useMemo } from 'react';

import type { WithSlotProps } from '~/components/hocs';
import type { DefineSlots } from '~/components/layout/utils';
import { withSlot } from '~/components/hocs';
import { componentRegistry, componentSlots, renderChild } from '~/components/layout/utils';
import { SlotsProvider } from '~/components/providers';
import { useTemplate } from '~/hooks';
import { cn } from '~/utils/cn';

export const NAME = 'TextInput';

export const Slots = componentSlots(NAME, {
  CONTAINER: 'container',
  LABEL: 'label',
  LABEL_TEXT: 'labelText',
  INPUT_WRAP: 'inputWrap',
  INPUT: 'input',
  ERRORS: 'errors',
});

type SlotProps = DefineSlots<
  typeof Slots,
  {
    [Slots.CONTAINER]: JSX.IntrinsicElements['div'];
    [Slots.LABEL]: JSX.IntrinsicElements['label'];
    [Slots.LABEL_TEXT]: JSX.IntrinsicElements['span'];
    [Slots.INPUT_WRAP]: JSX.IntrinsicElements['div'];
    [Slots.INPUT]: JSX.IntrinsicElements['input'];
    [Slots.ERRORS]: JSX.IntrinsicElements['div'];
  }
>;

export interface Props extends React.PropsWithChildren, WithSlotProps {
  className?: string;
  id?: string;
  name: string;
  value: string;
  label?: string;
  placeholder?: string;
  autoFocus?: boolean;
  touched: boolean;
  errors?: string[];
  containerRef?: React.Ref<HTMLDivElement>;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

const TextInputComponent = (props: Props) => {
  const { id, name, value, label, placeholder, autoFocus, className, errors, containerRef, onChange, onBlur, onFocus } =
    props;
  const template = useTemplate(NAME);

  const slots = useMemo<SlotProps>(
    () => ({
      [Slots.CONTAINER]: {
        className: cn(className, errors?.length && 'with-error'),
        ref: containerRef,
      },
      [Slots.LABEL]: {
        htmlFor: id,
      },
      [Slots.LABEL_TEXT]: {
        children: label ?? null,
      },
      [Slots.INPUT_WRAP]: {},
      [Slots.INPUT]: {
        id,
        name,
        value,
        placeholder,
        autoFocus,
        onChange,
        onBlur,
        onFocus,
      },
      [Slots.ERRORS]: {
        children: errors?.map((error, index) => (
          <li key={error}>
            {error}
            {index !== errors.length - 1 && ','}
          </li>
        )),
      },
    }),
    [id, name, value, label, placeholder, autoFocus, className, errors, containerRef, onChange, onBlur, onFocus]
  );

  return <SlotsProvider slots={slots}> {props.children ?? renderChild(template)}</SlotsProvider>;
};

TextInputComponent.displayName = NAME;

export const TextInput = withSlot(TextInputComponent);
componentRegistry.add(NAME, TextInput as React.FC);
